import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Box, Button, Card, FormHelperText, TextField, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../contexts/user-context";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useUpdateUser } from "../../api/use-user";
import { LoadingButton } from "@mui/lab";
import { ChangePassword } from "./change-password";
import PasswordIcon from "@mui/icons-material/Password";
import DeleteIcon from "@mui/icons-material/Delete";
import { ToastContext } from "../../contexts/toast-context";
import { DeleteProfile } from "./delete-profile.jsx";

export default function Profile() {
  const { t } = useTranslation(["common", "validation", "user"]);
  const { user, reload } = useContext(UserContext);
  const { showSuccess } = useContext(ToastContext);

  const schema = yup.object({
    name: yup
      .string()
      .max(120, t("maxLengthValidation", { field: t("name"), max: 120 }))
      .required(t("requiredValidation", { field: t("name") })),
    email: yup
      .string()
      .emailValidatorJs(t("emailValidation", { field: t("email") }))
      .required(t("requiredValidation", { field: t("email") })),
  });
  const dv = useMemo(
    () => ({
      name: user?.name || "",
      email: user?.email || ""
    }),
    [user]
  );

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
  } = useForm({
    defaultValues: dv,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset(dv);
  }, [reset, dv]);

  const [{ loading, error }, execute] = useUpdateUser(user?.id);
  const onSubmit = async (data) => {
    await execute({ data });
    await reload();
    showSuccess(t("successfulAction"));
  };
  const errorMessage =
    error &&
    (error.response?.data?.error_message ||
      t("genericError", { action: t("updateProfile") }));

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <Card
      sx={{
        px: 5,
        py: 3,
        display: "flex",
        flexDirection: "column",
        width: "480px",
        maxWidth: "480px",
        overflow: "auto",
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "26px",
            mt: "13px"
          }}
        >
          {t("profile")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mt: 4,
            "& > .MuiFormControl-root": {
              mb: 3,
            },
          }}
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                label={t("name")}
                {...field}
              />
            )}
            control={control}
            name="name"
          />
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
                label={t("email")}
                {...field}
              />
            )}
            control={control}
            name="email"
          />
          {errorMessage && (
            <FormHelperText error>{errorMessage}</FormHelperText>
          )}
          <LoadingButton
            type="submit"
            loading={loading}
            disabled={!isDirty}
            variant="contained"
          >
            {t("save")}
          </LoadingButton>
        </Box>
        <Button
          sx={{ mt: 3 }}
          onClick={() => setChangePasswordOpen(true)}
          startIcon={<PasswordIcon/>}
        >
          {t("changePassword")}
        </Button>
        <ChangePassword
          open={changePasswordOpen}
          userId={user?.id}
          onClose={() => setChangePasswordOpen(false)}
        />
        <Button
          sx={{ mt: 1 }}
          onClick={() => setDeleteOpen(true)}
          color="error"
          startIcon={<DeleteIcon/>}
        >
          {t("deleteProfile")}
        </Button>
        <DeleteProfile
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
        />
      </Box>
    </Card>
  );
}
